import React, {useEffect, useRef, useState} from "react";
import {createUseStyles} from "react-jss";
import {useInView} from "react-intersection-observer";
import {theme, Typography} from "@ekultur/vm";
import {VideoPlayer} from "../common/VideoPlayer";
import {FontTypes, Sizes} from "../helpers/enums";
import {BLOCK_UNLOCKED, useProgressDispatch} from "../progress/progressContext";
import {useContrastApproved} from "../helpers/useContrast";
import {useScrollytellTranslation} from "../Admin/scrollytellContext";

const useStyle = createUseStyles({
  videoBlock: {
    display: "inline-flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: ({ backgroundColor }) => backgroundColor,
    margin: ({ margin }) => margin || 0,
    padding: ({ padding }) => padding || 0,
    position: "relative"
  },
  caption: {
    color: ({ color }) => color,
    font: ({ font }) => font,
    display: "flex",
    marginTop: "17px",
  },
  image: {
    height: "100%",
    width: "100%",
    objectFit: "cover",
  },
});

export const VideoBlock = ({
  sectionId,
  containerId,
  blockId,
  block,
  className = "",
}) => {
  const {
    autoPlay = false,
    showControls = false,
    loop = false,
    size = Sizes.BASE,
    font = FontTypes.LIBRE_BASKERVILLE,
    margin,
    padding,
    caption,
    backgroundColor,
  } = block;
  const contrastApproved = useContrastApproved(
      backgroundColor !== undefined
          ? backgroundColor.substring(0, 7)
          : theme.colors.white,
      theme.colors.white
  )
  const classes = useStyle({
    margin,
    padding,
    backgroundColor,
    font,
    color: contrastApproved ? theme.colors.white : theme.colors.black
  });
  const t = useScrollytellTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0.9,
  });
  const videoRef = useRef();
  const progressDispatch = useProgressDispatch()

  useEffect(() => {
    if(videoRef?.current) {
      videoRef?.current.setAttribute('muted', '')
    }
  }, [videoRef])

  const playPromiseRef = useRef(null);

  useEffect(() => {
    if (autoPlay) {
      if(videoRef?.current) {
        videoRef?.current.setAttribute('muted', '')
      }
      if (inView && !isPlaying) {
        setIsPlaying(true);
        const promise = videoRef.current?.play();
        if (promise !== undefined) {
          playPromiseRef.current = promise;
          promise
            .then(() => {
              playPromiseRef.current = null;
            })
            .catch((error) => {
              playPromiseRef.current = null;
              setIsPlaying(false);
            });
        }
      }
      if (!inView && isPlaying) {
        setIsPlaying(false);
        if (playPromiseRef.current) {
          playPromiseRef.current
            .then(() => {
              videoRef.current?.pause();
            })
            .catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      }
    }
  }, [inView, autoPlay, isPlaying]);

  const handleVideoEnded = () => {
    if(block.locked) {
      progressDispatch({
        type: BLOCK_UNLOCKED,
        payload: {
          sectionId: sectionId,
          containerId: containerId,
          blockId: blockId
        }
      })
    }
  }

  const effectiveVideoUrl = block.videoUrl || block.url;
  const isUrl = typeof effectiveVideoUrl === "string" && (effectiveVideoUrl.startsWith("http://") || effectiveVideoUrl.startsWith("https://") || effectiveVideoUrl.startsWith("/"));
  
  let playerVideoUrl = "";
  let playerPoster = block.poster || "";

  if (isUrl) {
    playerVideoUrl = effectiveVideoUrl;
    if (!playerPoster && effectiveVideoUrl.includes("/streaming/adaptive/")) {
      const match = effectiveVideoUrl.match(/^(https?:\/\/[^/]+)\/streaming\/adaptive\/([^/]+)/);
      if (match) {
        playerPoster = `${match[1]}/image/${match[2]}?dimension=2048x2048`;
      }
    }
  } else if (effectiveVideoUrl) {
    playerVideoUrl = `https://ems.dimu.org/streaming/adaptive/${effectiveVideoUrl}/index.m3u8`;
    if (!playerPoster) {
      playerPoster = `https://ems.dimu.org/image/${effectiveVideoUrl}`;
    }
  }

  return (
      <figure className={`${classes.videoBlock} ${className}`} ref={ref}>
        {playerVideoUrl && (
          <div style={{ position: "relative", width: "100%" }}>
            <VideoPlayer
                autoPlay={autoPlay}
                videoUrl={playerVideoUrl}
                poster={playerPoster}
                ref={videoRef}
                controls={showControls}
                loop={loop}
                muted
                inView={inView}
                style={{
                  width: "100%",
                  objectFit: "contain",
                }}
                onEnded={handleVideoEnded}
            />
          </div>
        )}
        {caption && (
            <Typography tag="figcaption" size={size} className={classes.caption}>
              {caption}
            </Typography>
        )}
        {Boolean(block?.transcript && block.transcript.trim() && !block?.noSpeech) && (
          <div style={{ marginTop: "8px", width: "100%" }}>
            <button
              type="button"
              onClick={() => setShowTranscript((prev) => !prev)}
              aria-expanded={showTranscript}
              style={{
                background: "transparent",
                border: "none",
                color: "inherit",
                opacity: 0.85,
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "13px",
                padding: "4px 0",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {showTranscript
                ? t("VideoBlockHideTranscript", "Skjul transkripsjon")
                : t("VideoBlockShowTranscript", "Vis transkripsjon")}
            </button>
            {showTranscript && (
              <div
                style={{
                  marginTop: "6px",
                  padding: "10px 12px",
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px",
                  fontSize: "13px",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {block.transcript}
              </div>
            )}
          </div>
        )}
      </figure>
  )
};
